import React, { useState, useEffect, useRef } from 'react';
import Navbar from '@/components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Cloud, Sun, CloudRain, CloudSnow, Wind, Droplets, Thermometer, MapPin, RefreshCw, Search, X } from 'lucide-react';

function Tiempo() {
  const [weather, setWeather] = useState(null);
  const [forecast, setForecast] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [location, setLocation] = useState({ name: 'Palma de Mallorca', lat: 39.5696, lon: 2.6502 });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);

  // Ubicaciones predefinidas de Mallorca con coordenadas
  const predefinedLocations = [
    { name: 'Palma de Mallorca', lat: 39.5696, lon: 2.6502 },
    { name: 'Sóller', lat: 39.7667, lon: 2.7167 },
    { name: 'Pollença', lat: 39.8769, lon: 3.0156 },
    { name: 'Alcúdia', lat: 39.8533, lon: 3.1217 },
    { name: 'Valldemossa', lat: 39.7108, lon: 2.6231 },
    { name: 'Deià', lat: 39.7478, lon: 2.6481 },
    { name: 'Artà', lat: 39.6961, lon: 3.3519 },
    { name: 'Manacor', lat: 39.5697, lon: 3.2094 },
    { name: 'Felanitx', lat: 39.4697, lon: 3.1481 },
    { name: 'Santanyí', lat: 39.3564, lon: 3.1256 }
  ];

  // Buscar ciudades con Open-Meteo Geocoding API
  const searchCities = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearchLoading(true);
    try {
      const response = await fetch(
        `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(query)}&count=8&language=es&format=json`
      );
      const data = await response.json();
      
      if (data.results) {
        setSearchResults(data.results.map(r => ({
          name: `${r.name}${r.admin1 ? ', ' + r.admin1 : ''}${r.country ? ' (' + r.country + ')' : ''}`,
          displayName: r.name,
          lat: r.latitude,
          lon: r.longitude
        })));
      } else {
        setSearchResults([]);
      }
    } catch (err) {
      console.error('Error buscando ciudades:', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Debounce para la búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        searchCities(searchQuery);
        setShowResults(true);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Cerrar resultados al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Obtener descripción del código de tiempo
  const getWeatherDescription = (code) => {
    const descriptions = {
      0: 'Despejado',
      1: 'Mayormente despejado',
      2: 'Parcialmente nublado',
      3: 'Nublado',
      45: 'Niebla',
      48: 'Niebla con escarcha',
      51: 'Llovizna ligera',
      53: 'Llovizna moderada',
      55: 'Llovizna intensa',
      61: 'Lluvia ligera',
      63: 'Lluvia moderada',
      65: 'Lluvia intensa',
      71: 'Nieve ligera',
      73: 'Nieve moderada',
      75: 'Nieve intensa',
      80: 'Chubascos ligeros',
      81: 'Chubascos moderados',
      82: 'Chubascos intensos',
      95: 'Tormenta',
      96: 'Tormenta con granizo',
      99: 'Tormenta con granizo intenso'
    };
    return descriptions[code] || 'Variable';
  };

  const fetchWeather = async (loc) => {
    setLoading(true);
    setError(null);
    try {
      // Usar Open-Meteo API - gratuita y rápida
      const response = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${loc.lat}&longitude=${loc.lon}&current=temperature_2m,relative_humidity_2m,apparent_temperature,weather_code,wind_speed_10m&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max&timezone=Europe%2FMadrid&forecast_days=7`
      );
      
      if (!response.ok) throw new Error('Error al obtener el tiempo');
      const data = await response.json();
      
      setWeather({
        temp: Math.round(data.current.temperature_2m),
        feels_like: Math.round(data.current.apparent_temperature),
        humidity: data.current.relative_humidity_2m,
        wind: Math.round(data.current.wind_speed_10m),
        description: getWeatherDescription(data.current.weather_code),
        code: data.current.weather_code
      });
      
      // Pronóstico de los próximos días
      setForecast(data.daily.time.map((date, index) => ({
        date: date,
        maxTemp: Math.round(data.daily.temperature_2m_max[index]),
        minTemp: Math.round(data.daily.temperature_2m_min[index]),
        description: getWeatherDescription(data.daily.weather_code[index]),
        code: data.daily.weather_code[index],
        chanceOfRain: data.daily.precipitation_probability_max[index]
      })));
      
    } catch (err) {
      setError('No se pudo cargar el tiempo. Inténtalo de nuevo.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWeather(location);
  }, [location]);

  const selectLocation = (loc) => {
    setLocation(loc);
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
  };

  const getWeatherIcon = (code) => {
    // Open-Meteo weather codes
    if (code === 0 || code === 1) return <Sun className="h-12 w-12 text-yellow-400" />;
    if (code >= 2 && code <= 3) return <Cloud className="h-12 w-12 text-gray-400" />;
    if (code >= 45 && code <= 48) return <Cloud className="h-12 w-12 text-gray-500" />;
    if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return <CloudRain className="h-12 w-12 text-blue-400" />;
    if (code >= 71 && code <= 77) return <CloudSnow className="h-12 w-12 text-blue-200" />;
    if (code >= 95) return <CloudRain className="h-12 w-12 text-purple-400" />;
    return <Cloud className="h-12 w-12 text-gray-400" />;
  };

  const getSmallWeatherIcon = (code) => {
    if (code === 0 || code === 1) return <Sun className="h-6 w-6 text-yellow-400" />;
    if (code >= 2 && code <= 3) return <Cloud className="h-6 w-6 text-gray-400" />;
    if (code >= 45 && code <= 48) return <Cloud className="h-6 w-6 text-gray-500" />;
    if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return <CloudRain className="h-6 w-6 text-blue-400" />;
    if (code >= 71 && code <= 77) return <CloudSnow className="h-6 w-6 text-blue-200" />;
    if (code >= 95) return <CloudRain className="h-6 w-6 text-purple-400" />;
    return <Cloud className="h-6 w-6 text-gray-400" />;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const days = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`;
  };

  return (
    <div className="min-h-screen bg-dark-mk">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gold mb-2" data-testid="tiempo-title">
              El Tiempo
            </h1>
            <p className="text-gray-400">Planifica tus excursiones con el pronóstico</p>
          </div>
          <Button 
            className="btn-primary-mk"
            onClick={() => fetchWeather(location)}
            disabled={loading}
            data-testid="refresh-weather-btn"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>

        {/* Buscador de ciudades */}
        <div className="mb-6" ref={searchRef}>
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Buscar cualquier ciudad del mundo..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => searchQuery && setShowResults(true)}
              className="pl-10 pr-10 bg-gray-800 border-gray-700 text-white placeholder-gray-500"
              data-testid="search-city-input"
            />
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSearchResults([]);
                  setShowResults(false);
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            
            {/* Resultados de búsqueda */}
            {showResults && (searchResults.length > 0 || searchLoading) && (
              <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {searchLoading ? (
                  <div className="p-3 text-gray-400 text-center">Buscando...</div>
                ) : (
                  searchResults.map((result, index) => (
                    <button
                      key={index}
                      onClick={() => selectLocation(result)}
                      className="w-full text-left px-4 py-2 hover:bg-gray-700 text-white flex items-center gap-2 transition-colors"
                      data-testid={`search-result-${index}`}
                    >
                      <MapPin className="h-4 w-4 text-gold flex-shrink-0" />
                      <span className="truncate">{result.name}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* Ubicaciones predefinidas de Mallorca */}
        <div className="mb-6">
          <p className="text-sm text-gray-500 mb-2">Ubicaciones en Mallorca:</p>
          <div className="flex flex-wrap gap-2">
            {predefinedLocations.map((loc) => (
              <Button
                key={loc.name}
                variant={location.name === loc.name ? 'default' : 'outline'}
                className={location.name === loc.name ? 'btn-primary-mk' : 'border-gray-600 text-white hover:text-gold hover:border-gold'}
                size="sm"
                onClick={() => selectLocation(loc)}
                data-testid={`location-btn-${loc.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                <MapPin className="mr-1 h-3 w-3" />
                {loc.name}
              </Button>
            ))}
          </div>
        </div>

        {error && (
          <Card className="card-mk mb-8">
            <CardContent className="p-6 text-center text-red-400">
              {error}
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="loading-spinner"></div>
          </div>
        ) : weather && (
          <>
            {/* Tiempo actual */}
            <Card className="card-mk mb-4">
              <CardHeader>
                <CardTitle className="text-gold flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  {location.name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                  <div className="flex items-center gap-4">
                    {getWeatherIcon(weather.code)}
                    <div>
                      <p className="text-5xl font-bold text-white">{weather.temp}°C</p>
                      <p className="text-gray-400 capitalize">{weather.description}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-6 text-center">
                    <div>
                      <Thermometer className="h-6 w-6 mx-auto mb-2 text-orange-400" />
                      <p className="text-white font-semibold">{weather.feels_like}°C</p>
                      <p className="text-gray-500 text-sm">Sensación</p>
                    </div>
                    <div>
                      <Droplets className="h-6 w-6 mx-auto mb-2 text-blue-400" />
                      <p className="text-white font-semibold">{weather.humidity}%</p>
                      <p className="text-gray-500 text-sm">Humedad</p>
                    </div>
                    <div>
                      <Wind className="h-6 w-6 mx-auto mb-2 text-gray-400" />
                      <p className="text-white font-semibold">{weather.wind} km/h</p>
                      <p className="text-gray-500 text-sm">Viento</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Predicción para entreno exterior - justo después del tiempo actual */}
            <Card className="card-mk mb-8 border-gold/30">
              <CardContent className="p-4">
                <h3 className="text-gold font-semibold mb-2 flex items-center gap-2">
                  🏃 Predicción para tu entreno en el exterior
                </h3>
                <p className="text-gray-300">
                  {parseInt(weather.temp) > 30 
                    ? '🔥 Mucho calor. Entrena temprano por la mañana o al atardecer. Hidrátate constantemente.'
                    : parseInt(weather.temp) > 25 
                    ? '☀️ Calor moderado. Lleva agua y protección solar. Evita las horas centrales del día.'
                    : parseInt(weather.temp) < 10
                    ? '🥶 Frío. Calienta bien antes de empezar y abrígate con capas. Ideal para carreras largas.'
                    : parseInt(weather.temp) < 15
                    ? '🌬️ Fresquito. Ropa de abrigo ligera y algo impermeable por si acaso.'
                    : '✅ Temperatura perfecta para entrenar. ¡Aprovecha el día!'
                  }
                  {parseInt(weather.wind) > 30 && ' ⚠️ Viento fuerte, ten cuidado en zonas expuestas.'}
                  {parseInt(forecast[0]?.chanceOfRain) > 50 && ' 🌧️ Alta probabilidad de lluvia, lleva chubasquero.'}
                  {parseInt(forecast[0]?.chanceOfRain) > 20 && parseInt(forecast[0]?.chanceOfRain) <= 50 && ' 🌦️ Posibilidad de lluvia, ten un plan B.'}
                </p>
              </CardContent>
            </Card>

            {/* Pronóstico */}
            <h2 className="text-2xl font-bold text-white mb-4">Próximos días</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {forecast.map((day, index) => (
                <Card key={index} className="card-mk">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gold font-semibold">{formatDate(day.date)}</p>
                        <p className="text-gray-400 text-sm capitalize">{day.description}</p>
                      </div>
                      {getSmallWeatherIcon(day.code)}
                    </div>
                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-bold">{day.maxTemp}°</span>
                        <span className="text-gray-500">/</span>
                        <span className="text-gray-400">{day.minTemp}°</span>
                      </div>
                      {parseInt(day.chanceOfRain) > 20 && (
                        <div className="flex items-center gap-1 text-blue-400">
                          <CloudRain className="h-4 w-4" />
                          <span className="text-sm">{day.chanceOfRain}%</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Tiempo;
